import React, { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import SidebarNav from './Sidebar'
import MigrationBanner from '@/features/auth/components/MigrationBanner'
import QuotaUsageBanner from '@/components/banners/QuotaUsageBanner'
import { useViewportSize } from '@mantine/hooks'
import { Button } from '@/components/ui/button'
import { Menu as MenuIcon, Monitor, Lock } from 'lucide-react'
import { useFeatureBlocked } from '@/hooks/useFeatureBlocked'
import { useAppSelector } from '@/store'
import { selectRoles } from '@/store/slices/authSlice'
import { useAuth } from '@/hooks/useAuth'

const Layout: React.FC = () => {
    const { width } = useViewportSize()
    const isMobile = width < 768
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [showDesktopOverlay, setShowDesktopOverlay] = useState(!isMobile)
    const navigate = useNavigate()
    const { blocked, message } = useFeatureBlocked()
    const roles = useAppSelector(selectRoles)
    const isOwner = roles.includes('org_owner')
    const { logout } = useAuth()

    // Show desktop overlay for screens >= 768px
    if (!isMobile && showDesktopOverlay) {
        return (
            <div className="fixed inset-0 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-8 mx-4 max-w-md text-center">
                    <div className="mb-6">
                        <Monitor className="w-16 h-16 mx-auto text-blue-500 mb-4" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            Mobile Only
                        </h2>
                        <p className="text-gray-600">
                            This application is optimized for mobile devices.
                            Please use a mobile device or resize your browser
                            window to mobile view for the best experience.
                        </p>
                    </div>
                    <div className="space-y-3">
                        <Button
                            onClick={() => setShowDesktopOverlay(false)}
                            className="w-full"
                        >
                            Continue Anyway
                        </Button>
                        <p className="text-sm text-gray-500">
                            Recommended width: less than 768px
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    // Feature not enabled — non-owner members are fully blocked
    if (blocked && !isOwner) {
        return (
            <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
                <div className="max-w-md text-center px-6">
                    <div className="rounded-full bg-[#f5f5f5] p-4 mb-4 inline-flex">
                        <Lock className="h-8 w-8 text-[#616161]" />
                    </div>
                    <h2 className="text-xl font-[650] mb-2">Access Restricted</h2>
                    <p className="text-[13px] text-[#616161] font-[450] mb-2">
                        {message}
                    </p>
                    <p className="text-[12px] text-[#9e9e9e] font-[400]">
                        Your account and data are still safe. Once this feature is enabled for your organization, your access will be restored automatically.
                    </p>
                    <Button onClick={logout} className='mt-4 p-2'>
                        Back to sign in
                    </Button>
                </div>
            </div>
        )
    }

    return (
        <div className="flex h-screen bg-[#FAFAFA] relative">
            {/* Sidebar */}
            <div
                className={`fixed left-0 top-0 h-full z-30 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isMobile
                    ? sidebarOpen
                        ? 'translate-x-0'
                        : '-translate-x-full'
                    : 'translate-x-0'
                    }`}
                style={{
                    width: isMobile ? (sidebarOpen ? '240px' : '0px') : '240px',
                }}
            >
                <div className="w-full h-full">
                    <SidebarNav />
                </div>
            </div>

            {/* Mobile overlay */}
            {isMobile && sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-20"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main content */}
            <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
                {/* Mobile header */}
                {isMobile && (
                    <div className="h-14 flex items-center px-4 bg-white border-b sticky top-0 z-10 flex-shrink-0">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                                setSidebarOpen(!sidebarOpen)
                                console.log(sidebarOpen)
                            }}
                            className="mr-2 hover:bg-gray-100"
                        >
                            <MenuIcon className="w-6 h-6" />
                        </Button>
                        <img
                            src="/compra-text.svg"
                            alt="Logo"
                            className="w-32 ml-2"
                        />
                    </div>
                )}

                {/* Main content area */}
                <div className="flex-1 overflow-y-auto">
                    <div className="w-full max-w-[1078px] mx-auto px-4 pt-4 flex flex-col gap-3">
                        <MigrationBanner />
                        <QuotaUsageBanner />
                    </div>
                    <div className="w-full container mx-auto px-4 py-6">
                        <Outlet />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Layout
